import {
  BotMessage,
  BotMessageSubtype,
  Chat,
  ChatMessageLoadingStatus,
  ChatSummary,
  CustomerMessage,
  InProgressMessage,
  PersistentMessage,
  RecapMessage,
} from "@/types/chat"
import { DayObject } from "@/types/date"
import { PlaceSummary } from "@/types/place"
import {
  Room,
  TravelerGroup,
  Trip,
  TripChange,
  TripLogItem,
} from "@/types/trip"
import {
  CurrentUser,
  CurrentUserProfile,
  FollowStatus,
  PublicUser,
  UserLocation,
  UserPermission,
  UserRef,
} from "@/types/user"
import {
  CaseReducer,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import sortBy from "lodash/sortBy"
import uniqBy from "lodash/uniqBy"

import {
  PlaceSearchDateBucket,
  rememberPlaceSearchDateBucket,
} from "@/lib/place-search-memory"
import { PlaceDateKind } from "@/lib/place-utils"
import { assert } from "@/lib/utils/assertions"
import { freeze } from "@/lib/utils/collections"
import { empty } from "@/lib/utils/presence"
import { messageHasWebSearchResultsForMap } from "@/lib/web-search-results-for-map"
import {
  buildChatItemForMessage,
  buildChatItemsForTripLogItems,
  buildChatItemsForVoiceCalls,
} from "../chat-utils"
import { now } from "../date-utils"
import { isCustomerMessage, isPersistentBotMessage } from "../message-util"
import { getEmptyPlaceArray } from "../place-utils"
import { RootState } from "./redux-store"
import {
  fetchFollowersOrFollowing,
  loadMoreFollowersOrFollowing,
} from "./user-thunks"

export type SelectedPlaceSearchParams = {
  travelers?: TravelerGroup
  room?: Room
  stay?: PlaceSearchDateBucket
  day?: PlaceSearchDateBucket
}

export type UpdateSelectedPlaceSearchParamsPayload = Partial<
  SelectedPlaceSearchParams & {
    startDate?: DayObject
    endDate?: DayObject
    dateKind?: PlaceDateKind
  }
> & {
  source?: "tripSync"
}

/**
 * ChatState is the type for chat data stored in Redux state.
 * Unlike the API `Chat` type, `trip` is optional here because:
 * - At bootstrap, we don't have a trip yet
 * - Trip data is managed via selectTrip in trip-slice.ts (reads from RTK Query cache)
 * - selectChatTrip handles the optional case by returning undefined when chat.id === 0
 */
export type ChatState = Omit<Chat, "trip"> & { trip?: Trip }

const EmptyChat = freeze<ChatState>({
  id: 0,
  group: false,
  messages: [],
  log_items: [],
  more_messages: false,
  stream_ids: [],
  // trip is intentionally omitted - use selectTrip from trip-slice.ts to access trip data
})

export interface UserSliceState {
  user: CurrentUser | null
  userProfile: CurrentUserProfile
  userProfileLoaded: boolean
  chat: ChatState
  pendingCustomerMessage?: string
  inProgressMessage?: InProgressMessage
  // The stream that currently owns the single in-progress bubble. Lets a stopped
  // stream's late terminal event reconcile without clobbering a newer response.
  inProgressStreamId?: string
  // Streams the user has asked to stop. Excluded from isListeningToStreams so the
  // Stop button flips back to Send immediately, before the backend's terminal event.
  stoppedStreamIds: string[]
  // True when the user stopped the most recent response. Distinguishes a deliberate
  // stop (which can leave their message with no bot reply) from a genuine failure,
  // so we don't show the "didn't go through" error. Cleared on the next message.
  lastResponseStoppedByUser?: boolean
  // Bot-first turns have no unanswered customer message to signal a failure.
  lastResponseFailed?: boolean
  recapMessage?: RecapMessage

  messageLoadingStatus: ChatMessageLoadingStatus
  messagingDisabled?: boolean
  anonymousUserWasRateLimited?: boolean
  browser: string | null | undefined
  emailVerifyMode: boolean
  showClaimHandleDialog: boolean
  profileSetupDialogOpen: boolean
  assistantUpdateDialogOpen: boolean
  showCookiesBanner?: boolean
  cookiesBannerHeight: number

  // Permissions
  permissions: UserPermission[] | undefined

  followersDialog: {
    isOpen: boolean
    type: "followers" | "following" | null
    user: PublicUser | CurrentUser | null
    users: PublicUser[]
    isLoading: boolean
    page: number
    hasMore: boolean
  }
  followCounts: {
    [userRef: UserRef]: {
      followers: number
      following: number
    }
  }
  followStatus: Record<UserRef, FollowStatus>

  currentLocation: UserLocation | undefined

  selectedPlaceSearchParams: SelectedPlaceSearchParams | undefined
}

const setUser: CaseReducer<
  UserSliceState,
  PayloadAction<CurrentUser | null>
> = (state, action) => {
  state.user = action.payload
}

const patchUser: CaseReducer<
  UserSliceState,
  PayloadAction<Partial<CurrentUser>>
> = (state, action) => {
  if (state.user) {
    Object.assign(state.user, action.payload)
  }
}

const setBrowser: CaseReducer<
  UserSliceState,
  PayloadAction<string | null | undefined>
> = (state, action) => {
  state.browser = action.payload
}

export const setEmailVerifyMode: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.emailVerifyMode = action.payload
}

export const setShowClaimHandleDialog: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.showClaimHandleDialog = action.payload
}

export const setProfileSetupDialogOpen: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.profileSetupDialogOpen = action.payload
}

export const setAssistantUpdateDialogOpen: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.assistantUpdateDialogOpen = action.payload
}

export const setShowCookiesBanner: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.showCookiesBanner = action.payload
}

export const setCookiesBannerHeight: CaseReducer<
  UserSliceState,
  PayloadAction<number>
> = (state, action) => {
  state.cookiesBannerHeight = action.payload
}

const setUserProfile: CaseReducer<
  UserSliceState,
  PayloadAction<CurrentUserProfile>
> = (state, action) => {
  state.userProfile = action.payload
  state.userProfileLoaded = true
}

const setHasSetUpTravelAssistant: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.userProfile.has_set_up_travel_assistant = action.payload
  // Finishing setup floors memory depth at known on the server; Home should
  // not wait on the profile refetch to learn that.
  if (action.payload) state.userProfile.has_shallow_memory = false
}

const resetChat: CaseReducer<UserSliceState> = (state) => {
  userSlice.caseReducers.setChat(state, { type: "reset", payload: EmptyChat })
}

const setChat: CaseReducer<UserSliceState, PayloadAction<Chat | ChatState>> = (
  state,
  action
) => {
  const newChat = action.payload
  state.chat = newChat
  delete state.inProgressMessage
  delete state.inProgressStreamId
  state.stoppedStreamIds = []
  delete state.lastResponseStoppedByUser
  delete state.lastResponseFailed
}

const updateChat: CaseReducer<UserSliceState, PayloadAction<Partial<Chat>>> = (
  state,
  action
) => {
  const updatedChat = action.payload
  assert(
    updatedChat.id === state.chat.id,
    "use setChat() to change to a different chat"
  )
  state.chat = { ...state.chat, ...action.payload }
}

const setMessageLoadingStatus: CaseReducer<
  UserSliceState,
  PayloadAction<ChatMessageLoadingStatus>
> = (state, action) => {
  state.messageLoadingStatus = action.payload
}

const addStreamId: CaseReducer<UserSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  const streamId = action.payload
  delete state.lastResponseFailed
  if (!state.chat.stream_ids.includes(streamId)) {
    state.chat.stream_ids.push(streamId)
  }
}

const removeStreamId: CaseReducer<UserSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  const streamId = action.payload
  const index = state.chat.stream_ids.indexOf(streamId)
  if (index !== -1) {
    state.chat.stream_ids.splice(index, 1)
  }
  state.stoppedStreamIds = state.stoppedStreamIds.filter(
    (id) => id !== streamId
  )
}

// Group chats don't stream replies to the client, so a failure is reported by
// the trip broadcast that ends the response rather than by the stream itself.
const setGroupResponseFailed: CaseReducer<
  UserSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  if (action.payload) {
    state.lastResponseFailed = true
  } else {
    delete state.lastResponseFailed
  }
}

const markStreamStopped: CaseReducer<UserSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  const streamId = action.payload
  if (!state.stoppedStreamIds.includes(streamId)) {
    state.stoppedStreamIds.push(streamId)
  }
  state.lastResponseStoppedByUser = true
}

// Marks the frozen in-progress bubble as user-interrupted so it renders as a
// settled message with the "response was interrupted" note, ahead of the
// backend's terminal event.
const settleStoppedInProgressMessage: CaseReducer<
  UserSliceState,
  PayloadAction<string>
> = (state, action) => {
  if (state.inProgressStreamId === action.payload && state.inProgressMessage) {
    state.inProgressMessage.stopped_by_user = true
  }
}

const setPendingCustomerMessage: CaseReducer<
  UserSliceState,
  PayloadAction<string>
> = (state, action) => {
  assert(!state.pendingCustomerMessage, "a customer message is already pending")
  state.pendingCustomerMessage = action.payload
}

const clearPendingCustomerMessage: CaseReducer<UserSliceState> = (state) =>
  (state.pendingCustomerMessage = undefined)

const setRecapMessage: CaseReducer<
  UserSliceState,
  PayloadAction<RecapMessage | undefined>
> = (state, action) => {
  state.recapMessage = action.payload
}

const addMessage: CaseReducer<
  UserSliceState,
  PayloadAction<{ chatId: number; message: PersistentMessage }>
> = (state, action) => {
  if (state.chat.id !== action.payload.chatId) return
  const message = action.payload.message
  if (message.type === "customer") {
    if (message.body === state.pendingCustomerMessage) {
      state.pendingCustomerMessage = undefined
    }
    // A new question starts a fresh turn, so a prior stop no longer applies.
    delete state.lastResponseStoppedByUser
    delete state.lastResponseFailed
  }
  state.chat.messages.push(message)
  delete state.anonymousUserWasRateLimited
}

const addOrUpdateMessages: CaseReducer<
  UserSliceState,
  PayloadAction<{
    messages: PersistentMessage[]
    logItems?: TripLogItem[]
    serverHasOlderMessages?: boolean
  }>
> = (state, action) => {
  const newMessages = action.payload.messages || []
  const newLogItems = action.payload.logItems || []
  const oldMessages = state.chat.messages
  const oldLogItems = state.chat.log_items

  // Exclude any new messages or log items that we already have.
  const needMessage = (message: PersistentMessage) => {
    const existingMessage = oldMessages.find((m) => m.id === message.id)
    return (
      !existingMessage || existingMessage.lock_version < message.lock_version
    )
  }
  const needLogItem = (logItem: TripLogItem) =>
    !oldLogItems.find((tli) => tli.id === logItem.id)

  const messagesToAddOrUpdate = newMessages.filter(needMessage)
  const logItemsToAdd = newLogItems.filter(needLogItem)

  if (messagesToAddOrUpdate.length > 0) {
    const combinedMessages = uniqBy(
      // new messages come first so they take precedence in uniqBy's deduplication
      [...messagesToAddOrUpdate, ...oldMessages],
      (message) => message.id
    )
    state.chat.messages = sortBy(combinedMessages, (message) => message.id)
  }
  if (action.payload.serverHasOlderMessages !== undefined) {
    state.chat.more_messages = action.payload.serverHasOlderMessages
  }
  if (logItemsToAdd.length > 0) {
    state.chat.log_items ||= []
    const combinedLogItems = uniqBy(
      // new logItems come first so they take precedence in uniqBy's deduplication
      [...logItemsToAdd, ...oldLogItems],
      (logItem) => logItem.id
    )
    state.chat.log_items = sortBy(combinedLogItems, (logItem) => logItem.id)
  }
}

const removeMessage: CaseReducer<
  UserSliceState,
  PayloadAction<{ messageId: number }>
> = (state, action) => {
  state.chat.messages = state.chat.messages.filter(
    (message) => message.id !== action.payload.messageId
  )
}

const addInProgressMessageIfAbsent: CaseReducer<
  UserSliceState,
  PayloadAction<{
    chatId: number
    streamId: string
    sub_type?: BotMessageSubtype
  }>
> = (state, action) => {
  if (!state.inProgressMessage && state.chat.id === action.payload.chatId) {
    state.inProgressStreamId = action.payload.streamId
    state.inProgressMessage = {
      id: "in-progress",
      type: "bot",
      body: "",
      markers: [],
      places: [],
      inspirations: [],
      timestamp: now(),
      sub_type: action.payload.sub_type,
    }
  }
}

const removeInProgressMessageIfPresent: CaseReducer<
  UserSliceState,
  PayloadAction<number>
> = (state, action) => {
  const chatId = action.payload
  if (state.inProgressMessage && state.chat.id === chatId) {
    delete state.inProgressMessage
    delete state.inProgressStreamId
  }
}

const updateInProgressMessage: CaseReducer<
  UserSliceState,
  PayloadAction<{
    chatId: number
    message: InProgressMessage
    streamId?: string
  }>
> = (state, action) => {
  const { message, chatId, streamId } = action.payload
  if (chatId !== state.chat.id) return

  // A stopped stream keeps its bubble frozen — ignore any trailing updates it
  // emits before it notices the stop, so they can't unfreeze the message.
  if (streamId && state.stoppedStreamIds.includes(streamId)) return

  if (streamId) state.inProgressStreamId = streamId
  state.inProgressMessage = message

  // The in-progress message may also carry an updated chat or trip.
  if (message.chat) {
    state.chat.name = message.chat.name
    state.chat.group = message.chat.group
    state.chat.trip = message.chat.trip
  }
  if (message.trip) {
    state.chat.trip = message.trip
  }
}

const updatePlaceInMessages: CaseReducer<
  UserSliceState,
  PayloadAction<PlaceSummary>
> = (state, action) => {
  const newPlace = action.payload
  state.chat.messages.forEach((message) => {
    if (!isPersistentBotMessage(message)) return

    const index = message.places.findIndex(
      (e) => e.entity_ref === newPlace.entity_ref
    )
    if (index !== -1) {
      message.places[index] = newPlace
    }
  })
}

const stopMessageStreaming: CaseReducer<
  UserSliceState,
  PayloadAction<{
    chat: ChatSummary
    finalMessage: BotMessage
  }>
> = (state, action) => {
  const message = action.payload.finalMessage
  if (!isSupersededStream(state, message.stream_id)) {
    delete state.inProgressMessage
    delete state.inProgressStreamId
  }

  if (state.chat.id === action.payload.chat.id) {
    if (!isSupersededStream(state, message.stream_id)) {
      delete state.lastResponseFailed
    }
    // TODO: async calls may get here after the current chat has been
    // changed to something else, so check the id before applying
    // updates. It would be nice to have a cleaner solution to this.
    userSlice.caseReducers.updateChat(state, {
      type: action.type,
      payload: action.payload.chat,
    })

    // Add the final message, unless we already have it for some
    // reason (e.g. an async broadcast). A stopped response can land after the
    // customer has already sent their next message, so insert by id rather
    // than appending.
    const alreadyExists = state.chat.messages.some((m) => m.id === message.id)
    if (!alreadyExists) {
      const successor = state.chat.messages.findIndex((m) => m.id > message.id)
      if (successor === -1) {
        state.chat.messages.push(message)
      } else {
        state.chat.messages.splice(successor, 0, message)
      }
    }
  }
}

const stopMessageStreamingWithError: CaseReducer<
  UserSliceState,
  PayloadAction<string>
> = (state, action) => {
  if (!isSupersededStream(state, action.payload)) {
    delete state.inProgressMessage
    delete state.inProgressStreamId
    if (state.chat.stream_ids.includes(action.payload)) {
      state.lastResponseFailed = true
    }
  }
}

const stopMessageStreamingAndDisable: CaseReducer<UserSliceState> = (state) => {
  delete state.inProgressMessage
  delete state.inProgressStreamId
  state.messagingDisabled = true
}

const stopMessageStreamingWithCancel: CaseReducer<
  UserSliceState,
  PayloadAction<string>
> = (state, action) => {
  if (!isSupersededStream(state, action.payload)) {
    delete state.inProgressMessage
    delete state.inProgressStreamId
  }
}

// A stopped stream's terminal event can arrive after the user has already sent a
// new message that took over the in-progress bubble. In that case the old
// stream's cleanup must not touch the newer response's bubble.
const isSupersededStream = (
  state: UserSliceState,
  streamId: string | undefined
) =>
  state.inProgressStreamId !== undefined &&
  state.inProgressStreamId !== streamId

const setTripChangeInFinalBotMessage: CaseReducer<
  UserSliceState,
  PayloadAction<{ messageId: number; change: TripChange }>
> = (state, action) => {
  const { messageId, change } = action.payload
  const messages = state.chat.messages.filter(isPersistentBotMessage)
  const message = messages.reverse().find((m) => m.id === messageId)
  if (message && !message.trip_change) {
    message.trip_change = change
  }
}

const enableChatMessages: CaseReducer<UserSliceState> = (state) => {
  delete state.messagingDisabled
}

const setAnonymousUserWasRateLimited: CaseReducer<UserSliceState> = (state) => {
  state.anonymousUserWasRateLimited = true
}

const selectSliceState = (state: RootState) => state.userSlice

export const selectBrowser = createSelector(
  [selectSliceState],
  (state) => state.browser
)

const setUserPermissions: CaseReducer<
  UserSliceState,
  PayloadAction<UserPermission[]>
> = (state, action) => {
  state.permissions = action.payload
}

const setCurrentLocation: CaseReducer<
  UserSliceState,
  PayloadAction<UserLocation | undefined>
> = (state, action) => {
  state.currentLocation = action.payload
}

export const userSliceInitialState: UserSliceState = {
  user: {
    user_ref: "u-fake",
    time_zone: "America/Los_Angeles",
    provider: "",
    has_persona: false,
    created_at: now(),
    uuid: "",
    is_mindtrip: false,
    is_crawler: false,
    email_validated: false,
    has_thatch_data: false,
    is_from_thatch: false,
    is_thatch_creator: false,
  },
  userProfile: {},
  userProfileLoaded: false,
  chat: EmptyChat,
  stoppedStreamIds: [],
  messageLoadingStatus: "idle",
  permissions: [],
  browser: undefined,
  emailVerifyMode: false,
  showClaimHandleDialog: false,
  profileSetupDialogOpen: false,
  assistantUpdateDialogOpen: false,
  showCookiesBanner: false,
  cookiesBannerHeight: 0,
  followersDialog: {
    isOpen: false,
    type: null,
    user: null,
    users: [],
    isLoading: false,
    page: 1,
    hasMore: true,
  },
  followCounts: {},
  followStatus: {},
  currentLocation: undefined,
  selectedPlaceSearchParams: undefined,
}

export const userSlice = createSlice({
  name: "user",
  initialState: userSliceInitialState,
  reducers: {
    setUser,
    patchUser,
    setUserProfile,
    setHasSetUpTravelAssistant,
    setChat,
    resetChat,
    updateChat,
    setMessageLoadingStatus,
    addStreamId,
    removeStreamId,
    markStreamStopped,
    setGroupResponseFailed,
    settleStoppedInProgressMessage,
    setPendingCustomerMessage,
    clearPendingCustomerMessage,
    setRecapMessage,
    addMessage,
    addOrUpdateMessages,
    removeMessage,
    addInProgressMessageIfAbsent,
    removeInProgressMessageIfPresent,
    updateInProgressMessage,
    updatePlaceInMessages,
    stopMessageStreaming,
    stopMessageStreamingWithError,
    stopMessageStreamingAndDisable,
    stopMessageStreamingWithCancel,
    setTripChangeInFinalBotMessage,
    enableChatMessages,
    setAnonymousUserWasRateLimited,
    setUserPermissions,
    setBrowser,
    setEmailVerifyMode,
    setShowClaimHandleDialog,
    setProfileSetupDialogOpen,
    setAssistantUpdateDialogOpen,
    setShowCookiesBanner,
    setCookiesBannerHeight,
    setCurrentLocation,

    openFollowersDialog: (
      state,
      action: PayloadAction<{
        user: PublicUser | CurrentUser
        type: "followers" | "following"
      }>
    ) => {
      state.followersDialog = {
        ...state.followersDialog,
        isOpen: true,
        type: action.payload.type,
        user: action.payload.user,
        isLoading: true,
        users: [],
      }
    },
    closeFollowersDialog: (state) => {
      state.followersDialog = {
        ...userSliceInitialState.followersDialog,
      }
    },
    setFollowersDialogUsers: (state, action: PayloadAction<PublicUser[]>) => {
      state.followersDialog.users = action.payload
      state.followersDialog.isLoading = false
    },
    setFollowCounts: (
      state,
      action: PayloadAction<{
        userRef: UserRef
        followers: number
        following: number
      }>
    ) => {
      const { userRef, followers, following } = action.payload
      state.followCounts[userRef] = { followers, following }
    },
    updateFollowersCount: (
      state,
      action: PayloadAction<{ userRef: UserRef; increment: boolean }>
    ) => {
      const { userRef, increment } = action.payload
      if (state.followCounts[userRef]) {
        state.followCounts[userRef].followers += increment ? 1 : -1
      }
    },
    updateFollowingCount: (
      state,
      action: PayloadAction<{ userRef: UserRef; increment: boolean }>
    ) => {
      const { userRef, increment } = action.payload
      if (state.followCounts[userRef]) {
        state.followCounts[userRef].following += increment ? 1 : -1
      }
    },
    setFollowStatus: (
      state,
      action: PayloadAction<{ userRef: UserRef; followStatus: FollowStatus }>
    ) => {
      state.followStatus[action.payload.userRef] = action.payload.followStatus
    },
    setSelectedPlaceSearchParams: (
      state,
      action: PayloadAction<SelectedPlaceSearchParams | undefined>
    ) => {
      state.selectedPlaceSearchParams = action.payload
    },
    updateSelectedPlaceSearchParams: (
      state,
      action: PayloadAction<UpdateSelectedPlaceSearchParamsPayload>
    ) => {
      const { source: _source, ...payload } = action.payload
      if (!state.selectedPlaceSearchParams) {
        state.selectedPlaceSearchParams = {}
      }
      if (payload.travelers !== undefined) {
        state.selectedPlaceSearchParams.travelers = payload.travelers
      }
      if (payload.room !== undefined) {
        state.selectedPlaceSearchParams.room = payload.room
      }
      if (payload.stay !== undefined) {
        state.selectedPlaceSearchParams.stay = payload.stay
      }
      if (payload.day !== undefined) {
        state.selectedPlaceSearchParams.day = payload.day
      }
      if (
        payload.startDate !== undefined &&
        payload.endDate !== undefined &&
        payload.dateKind !== undefined
      ) {
        const bucket = payload.dateKind === "stay" ? "stay" : "day"
        const existing = state.selectedPlaceSearchParams[bucket]
        state.selectedPlaceSearchParams[bucket] = rememberPlaceSearchDateBucket(
          payload.startDate,
          payload.endDate,
          existing
        )
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFollowersOrFollowing.pending, (state) => {
      state.followersDialog.isLoading = true
      state.followersDialog.page = 1
    })
    builder.addCase(
      fetchFollowersOrFollowing.fulfilled,
      (
        state,
        action: PayloadAction<{ users: PublicUser[]; hasMore: boolean }>
      ) => {
        state.followersDialog.users = action.payload.users
        state.followersDialog.isLoading = false
        state.followersDialog.hasMore = action.payload.hasMore
      }
    )
    builder.addCase(fetchFollowersOrFollowing.rejected, (state) => {
      state.followersDialog.isLoading = false
      state.followersDialog.users = []
      state.followersDialog.hasMore = false
    })

    builder.addCase(loadMoreFollowersOrFollowing.pending, (state) => {
      state.followersDialog.isLoading = true
    })
    builder.addCase(
      loadMoreFollowersOrFollowing.fulfilled,
      (
        state,
        action: PayloadAction<{ users: PublicUser[]; hasMore: boolean }>
      ) => {
        state.followersDialog.users = [
          ...state.followersDialog.users,
          ...action.payload.users,
        ]
        state.followersDialog.isLoading = false
        state.followersDialog.hasMore = action.payload.hasMore
        state.followersDialog.page += 1
      }
    )
    builder.addCase(loadMoreFollowersOrFollowing.rejected, (state) => {
      state.followersDialog.isLoading = false
    })
  },
})

export const selectUser = createSelector([selectSliceState], (state) => {
  assert(state.user, "user is missing from user-slice")
  return state.user
})

export const selectUserIsAnonymous = createSelector([selectUser], (user) => {
  return user.provider === "anonymous"
})

// False until the session lands (the initial user has no provider) and for
// anonymous sessions, so a gate on it never fires a fetch on the first render.
export const selectUserIsSignedIn = createSelector([selectUser], (user) => {
  return user.provider !== "" && user.provider !== "anonymous"
})

export const selectUserIsEmail = createSelector([selectUser], (user) => {
  return user.provider === "email"
})

export const selectUserGreetingName = createSelector([selectUser], (user) => {
  return user.first_name
    ? `${user.first_name} ${user.last_name || ""}`
    : "Hello, Traveler"
})

export const selectUserTimeZone = createSelector(
  [selectUser],
  (user) => user.time_zone
)

export const selectUserLanguage = createSelector(
  [selectUser],
  (user) => user.language
)

export const selectUserRegion = createSelector(
  [selectUser],
  (user) => user.region
)

export const selectCurrentUserHandle = createSelector([selectUser], (user) => {
  return user.handle
})

export const selectCurrentUserRef = createSelector([selectUser], (user) => {
  return user.user_ref
})

export const selectHasAnyTrip = createSelector(
  [selectUser],
  (user) => !!user.has_any_trip
)

export const selectHasChatted = createSelector(
  [selectUser],
  (user) => !!user.has_chatted
)

export const selectHasSavedPlaces = createSelector(
  [selectUser],
  (user) => !!user.has_saved_places
)

export const selectFirstName = createSelector([selectUser], (user) => {
  return user.first_name
    ? `${user.first_name.charAt(0).toUpperCase()}${user.first_name.slice(1)}`
    : undefined
})

export const selectEmail = createSelector([selectUser], (user) => {
  return user.email
})

export const selectEmailVerified = createSelector([selectUser], (user) => {
  return user.email_validated
})

export const selectIsMindtripUser = createSelector(
  [selectUser],
  (user) => user?.is_mindtrip
)

export const selectIsUserCrawler = createSelector(
  [selectUser],
  (user) => user?.is_crawler
)

export const selectUserProfile = createSelector(
  [selectSliceState],
  (state) => state.userProfile
)

export const selectUserProfileLoaded = createSelector(
  [selectSliceState],
  (state) => state.userProfileLoaded
)

// Account-level long-term memory switch (the unified flag). Absent ⇒ on.
export const selectAccountMemoryEnabled = createSelector(
  [selectUserProfile],
  (userProfile) => userProfile.memory_enabled ?? true
)

export const selectUserCurrencyCode = createSelector(
  [selectUserProfile],
  (userProfile) => userProfile?.currency_code
)

export const selectUserMeasurementSystem = createSelector(
  [selectUserProfile],
  (userProfile) => userProfile?.measurement_system
)

const selectChat = createSelector([selectSliceState], (state) => state.chat)

export const selectInitialChat = createSelector(
  [selectChat],
  (chat) => chat.initial_chat
)

export const selectIsGroupChat = createSelector(
  [selectChat],
  (chat) => chat.group
)

export const selectChatName = createSelector([selectChat], (chat) => chat.name)

export const selectChatId = createSelector([selectChat], (chat) => chat.id)

export const selectChatOrgid = createSelector(
  [selectChat],
  (chat) => chat.orgid
)

export const selectChatAnonymousSharing = createSelector(
  [selectChat],
  (chat) => chat.anonymous_sharing ?? false
)

export const selectChatMemoryEnabled = createSelector(
  [selectChat],
  (chat) => chat.memory_enabled ?? true
)

export const selectChatMemoryEditable = createSelector(
  [selectChat],
  (chat) => chat.memory_setting_editable ?? false
)

export const selectStreamIds = createSelector(
  [selectChat],
  (chat) => chat.stream_ids
)

export const selectEmailVerifyMode = createSelector(
  [selectSliceState],
  (state) => state.emailVerifyMode
)

export const selectShowClaimHandleDialog = createSelector(
  [selectSliceState],
  (state) => state.showClaimHandleDialog
)

export const selectProfileSetupDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.profileSetupDialogOpen
)

export const selectAssistantUpdateDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.assistantUpdateDialogOpen
)

export const selectShowCookiesBanner = createSelector(
  [selectSliceState],
  (state) => state.showCookiesBanner
)

export const selectCookiesBannerHeight = createSelector(
  [selectSliceState],
  (state) => state.cookiesBannerHeight
)

export const selectStoppedStreamIds = createSelector(
  [selectSliceState],
  (state) => state.stoppedStreamIds
)

export const isListeningToStreams = createSelector(
  [selectStreamIds, selectStoppedStreamIds],
  (streamIds, stoppedStreamIds) => {
    return streamIds.some((id) => !stoppedStreamIds.includes(id))
  }
)

export const selectMessageLoadingStatus = createSelector(
  [selectSliceState],
  (state) => state.messageLoadingStatus
)

export const selectIsLoadingMessages = createSelector(
  selectMessageLoadingStatus,
  (status) => status === "loading-old" || status === "loading-new"
)

export const selectPersistentMessages = createSelector(
  [selectChat],
  (chat) => chat.messages
)

const selectRecapMessage = createSelector(
  selectSliceState,
  (state) => state.recapMessage
)

export const selectMessagesWithRecap = createSelector(
  [selectPersistentMessages, selectRecapMessage],
  (persistentMessages, recapMessage) => {
    if (recapMessage && !empty(persistentMessages)) {
      const allMessages: Array<PersistentMessage | RecapMessage> = [
        ...persistentMessages,
      ]
      allMessages.push(recapMessage)
      return sortBy(allMessages, (message) => message.timestamp.epoch_millis)
    } else {
      return persistentMessages
    }
  }
)

const selectLogItems = createSelector([selectChat], (chat) => chat.log_items)

export const selectLogItemCount = createSelector(
  [selectLogItems],
  (logItems) => logItems.length
)

export const selectChatItems = createSelector(
  [selectMessagesWithRecap, selectLogItems],
  (messages, tripLogItems) =>
    sortBy(
      [
        ...messages.map((m) => buildChatItemForMessage(m)),
        ...buildChatItemsForTripLogItems(tripLogItems),
        ...buildChatItemsForVoiceCalls(messages),
      ],
      (item) => item.timestamp.epoch_millis,
      // A voice-call record carries the final timestamp of the run it closes,
      // so it has to lose that tie or it renders above its own turns.
      (item) => (item.type === "voice-call" ? 1 : 0)
    )
)

export const selectHasChatItems = createSelector(
  [selectChatItems],
  (chatItems) => chatItems.length > 0
)

// Returns true if the server has older messages that we haven't loaded yet,
// false if not, or undefined if we don't know yet.
export const selectServerHasOlderMessages = createSelector(
  [selectChat],
  (chat) => chat.more_messages
)

export const selectFirstLoadedMessageId = createSelector(
  [selectPersistentMessages],
  (messages) => messages && messages[0]?.id
)

export const selectLastLoadedMessageId = createSelector(
  [selectPersistentMessages],
  (messages) => messages[messages.length - 1]?.id || undefined
)

export const selectHasPersistentMessages = createSelector(
  [selectPersistentMessages],
  (messages) => messages.length > 0
)

export const selectLoadedPersistentMessageCount = createSelector(
  [selectPersistentMessages],
  (messages) => messages.length
)

export const selectLastPersistentMessage = createSelector(
  [selectPersistentMessages],
  (messages) => (messages.length ? messages[messages.length - 1] : undefined)
)

export const selectCustomerMessages = createSelector(
  [selectPersistentMessages],
  (messages) => {
    return messages.filter(isCustomerMessage)
  }
)

export const selectCustomerMessagesReversed = createSelector(
  [selectCustomerMessages],
  (messages) => [...messages].reverse()
)

export const selectLastCustomerMessageByCurrentUser = createSelector(
  [selectCustomerMessagesReversed, selectCurrentUserRef],
  (messages, currentUserRef) =>
    messages.find((m) => m.user?.user_ref === currentUserRef)
)

export const selectLoadedCustomerMessageCount = createSelector(
  [selectCustomerMessages],
  (customerMessages) => customerMessages.length
)

export const selectLastCustomerMessage = createSelector(
  [selectCustomerMessages],
  (messages): CustomerMessage | undefined => messages[messages.length - 1]
)

export const selectPersistentBotMessages = createSelector(
  [selectPersistentMessages],
  (messages) => {
    return messages.filter(isPersistentBotMessage)
  }
)

const selectPersistentNonFollowUpBotMessages = createSelector(
  [selectPersistentBotMessages],
  (messages) => messages.filter((m) => m.sub_type !== "follow-up")
)

export const selectPersistentNonFollowUpBotMessagesCount = createSelector(
  [selectPersistentNonFollowUpBotMessages],
  (messages) => messages.length
)

export const selectLastPersistentBotMessage = createSelector(
  [selectPersistentBotMessages],
  (messages) => messages[messages.length - 1]
)

export const selectLastPersistentNonFollowUpBotMessage = createSelector(
  [selectPersistentNonFollowUpBotMessages],
  (messages) => messages[messages.length - 1]
)

export const selectLastPersistentBotMessageWithTripChanges = createSelector(
  [selectPersistentNonFollowUpBotMessages],
  (messages) => [...messages].reverse().find((m) => m.trip_change)
)

export const selectLastBotMessageTypesOfInterest = createSelector(
  [selectLastPersistentBotMessage],
  (message) => message?.types_of_interest
)

export const selectLastIntent = createSelector(
  [selectPersistentBotMessages],
  (messages) => {
    const messagesWithIntents = messages.filter((m) => m.intent)
    const count = messagesWithIntents.length
    return messagesWithIntents[count - 1]?.intent
  }
)

export const selectLastIntentCode = createSelector(
  [selectLastIntent],
  (intent) => intent?.code
)

export const selectLastResponseStoppedByUser = createSelector(
  [selectSliceState],
  (state) => state.lastResponseStoppedByUser
)

export const selectLastBotMessageFailed = createSelector(
  [
    selectIsGroupChat,
    isListeningToStreams,
    selectLastPersistentMessage,
    selectLastResponseStoppedByUser,
    (state: RootState) => selectSliceState(state).lastResponseFailed,
  ],
  (isGroupChat, isListening, lastPersistentMessage, stoppedByUser, failed) => {
    // A group chat's last message is routinely a customer message the bot was
    // never asked to answer, so only an explicit failure counts there.
    if (isGroupChat) return !!failed

    return !!(
      !isListening &&
      !stoppedByUser &&
      ((!lastPersistentMessage && failed) ||
        (lastPersistentMessage?.type === "customer" &&
          lastPersistentMessage.voice_message !== true))
    )
  }
)

export const selectChatMessagesDisabled = createSelector(
  [selectSliceState],
  (state) => state.messagingDisabled
)

export const selectIsCustomerMessagePending = createSelector(
  [selectSliceState],
  (state) => !!state.pendingCustomerMessage
)

export const selectInProgressMessage = createSelector(
  [selectSliceState],
  (state) => state.inProgressMessage
)

export const selectInProgressMessageLength = createSelector(
  [selectInProgressMessage],
  (inProgressMessage) => inProgressMessage?.body.length
)

const selectNonFollowUpInProgressMessage = createSelector(
  [selectSliceState],
  (state) => {
    return state.inProgressMessage?.sub_type !== "follow-up"
      ? state.inProgressMessage
      : undefined
  }
)

export const selectHasInProgressMessage = createSelector(
  [selectInProgressMessage],
  (inProgressMessage) => !!inProgressMessage
)

export const selectLoadedMessageCount = createSelector(
  [selectPersistentMessages, selectHasInProgressMessage],
  (persistentMessages, hasInProgressMessage) => {
    return persistentMessages.length + (hasInProgressMessage ? 1 : 0)
  }
)

export const selectAnonymousUserWasRateLimited = createSelector(
  [selectSliceState],
  (state) => state.anonymousUserWasRateLimited
)

export const selectChatTrip = createSelector([selectChat], (chat) => chat.trip)

export const selectCurrentLocation = createSelector(
  [selectSliceState],
  (state) => state.currentLocation
)

export const selectCurrentLocationEntityRef = createSelector(
  [selectCurrentLocation],
  (currentLocation) => currentLocation?.entity_ref
)

export const selectChatTripDestinationRef = createSelector(
  [selectChatTrip],
  (trip) => trip?.destination?.entity_ref
)

export const selectLocationOfInterest = createSelector(
  [selectChatTripDestinationRef, selectCurrentLocationEntityRef],
  (destinationRef, currentLocationRef) => {
    return destinationRef || currentLocationRef
  }
)

const selectPlacesMentionedInPersistentBotMessages = createSelector(
  [selectPersistentBotMessages],
  (messages) => {
    return messages.map((message) => message.places).flat()
  }
)

const selectPlacesMentionedInInProgressMessage = createSelector(
  [selectInProgressMessage],
  (message) => {
    return message?.places
  }
)

const selectNonUniqueMentionedPlaces = createSelector(
  [
    selectPlacesMentionedInPersistentBotMessages,
    selectPlacesMentionedInInProgressMessage,
  ],
  (persistentPlaces, inProgressPlaces) => {
    if (inProgressPlaces) {
      return inProgressPlaces
        ? persistentPlaces.concat(inProgressPlaces)
        : persistentPlaces
    } else {
      return persistentPlaces
    }
  }
)

export const selectMentionedPlaces = createSelector(
  [selectNonUniqueMentionedPlaces],
  (nonUniquePlaces) => uniqBy(nonUniquePlaces, (place) => place.entity_ref)
)

export const selectLatestNonFollowUpMessageWithPlaces = createSelector(
  [selectPersistentNonFollowUpBotMessages, selectNonFollowUpInProgressMessage],
  (persistentNonFollowUpMessages, nonFollowUpInProgressMessage) => {
    if (nonFollowUpInProgressMessage?.places.length) {
      return nonFollowUpInProgressMessage
    } else {
      return [...persistentNonFollowUpMessages]
        .reverse()
        .find((message) => message.places.length > 0)
    }
  }
)

export const selectLatestNonFollowUpMessageWithStructuredResults =
  createSelector(
    [
      selectPersistentNonFollowUpBotMessages,
      selectNonFollowUpInProgressMessage,
    ],
    (persistentNonFollowUpMessages, nonFollowUpInProgressMessage) => {
      if (
        nonFollowUpInProgressMessage &&
        messageHasWebSearchResultsForMap(nonFollowUpInProgressMessage)
      ) {
        return nonFollowUpInProgressMessage
      }
      return [...persistentNonFollowUpMessages]
        .reverse()
        .find(messageHasWebSearchResultsForMap)
    }
  )

export const selectPlacesOfLatestMessageWithPlaces = createSelector(
  [selectLatestNonFollowUpMessageWithPlaces],
  (message) => {
    // Return a constant empty object if we don't have any places
    // instead of returning [], because [] makes a new empty array
    // every time we get to this line, which defeats memoization and
    // leads to unnecessary re-renders.
    if (!message) return getEmptyPlaceArray()
    return message.places || getEmptyPlaceArray()
  }
)

export const selectLatestMessagePlaceCount = createSelector(
  [selectPlacesOfLatestMessageWithPlaces],
  (places) => places.length
)

export const selectFirstLatestMessagePlace = createSelector(
  [selectPlacesOfLatestMessageWithPlaces],
  (places) => places[0] || undefined
)

export const selectIdForLastestMessageWithPlaces = createSelector(
  [selectLatestNonFollowUpMessageWithPlaces],
  (message) => message?.id
)

// permissions
export const selectUserPermissions = createSelector(
  [selectSliceState],
  (state) => state.permissions
)

export const selectCanJoinAsCreatorPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_join_as_creator")
)

export const selectCanPublishVanityLinkPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_publish_vanity_link")
)

export const selectCanViewVanityReportsPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_view_vanity_reports")
)

export const selectCanViewAllVanityReportsPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_view_all_vanity_reports")
)

export const selectCanAdminVanityLinkPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_admin_vanity_link")
)

export const selectCanPublishInspiration = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_publish_inspiration")
)

export const selectCanAdminInspiration = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_admin_inspiration")
)

export const selectCanAccessDashboard = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_access_dashboard")
)

export const selectCanManageOrganizations = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_manage_organizations")
)

export const selectSelectedPlaceSearchParams = createSelector(
  [selectSliceState],
  (state) => state.selectedPlaceSearchParams
)

export const selectSelectedPlaceTravelers = createSelector(
  [selectSelectedPlaceSearchParams],
  (searchParams) => searchParams?.travelers
)

export const selectSelectedPlaceRoom = createSelector(
  [selectSelectedPlaceSearchParams],
  (searchParams) => searchParams?.room
)

export type UserSlice = typeof userSlice

export const {
  openFollowersDialog,
  closeFollowersDialog,
  setFollowersDialogUsers,
} = userSlice.actions

export const selectFollowersDialog = createSelector(
  [selectSliceState],
  (state) => state.followersDialog
)

export const selectFollowersDialogOpen = createSelector(
  [selectFollowersDialog],
  (followersDialog) => followersDialog.isOpen
)

// Unlike the two count selectors below, this preserves the difference between
// "we have not loaded this user's counts" and "this user really has none", so
// callers can avoid rendering an unloaded count as a confident 0.
export const selectFollowCounts = createSelector(
  [selectSliceState, (_state, userRef: UserRef) => userRef],
  (state, userRef) => state.followCounts[userRef]
)

export const selectFollowersCount = createSelector(
  [selectSliceState, (_state, userRef: UserRef) => userRef],
  (state, userRef) => state.followCounts[userRef]?.followers ?? 0
)

export const selectFollowingCount = createSelector(
  [selectSliceState, (_state, userRef: UserRef) => userRef],
  (state, userRef) => state.followCounts[userRef]?.following ?? 0
)

export const selectFollowStatus = createSelector(
  [selectSliceState, (_state, userRef: UserRef) => userRef],
  (state, userRef) => state.followStatus[userRef]
)

export default userSlice
